import React from 'react'
import {
  ToastProvider as ToastNotificationsToastProvider,
  ToastProps,
  ToastProviderProps,
  ToastContainerProps,
} from 'react-toast-notifications'
import { Alert } from '@mui/material'
import { MonthlySummaryToast } from './MonthlySummaryToast'
import { MonthlySummaryNotificationData } from './types'

type ExtendedToastProps = ToastProps & {
  data?: MonthlySummaryNotificationData
  onSnooze?: () => void
  onCreateMonthlySummaryNote?: () => void
}

const isValidMonthlySummaryData = (data: unknown): data is MonthlySummaryNotificationData => {
  if (!data || typeof data !== 'object' || data === null) {
    return false
  }

  const obj = data as Record<string, unknown>
  return 'uid' in obj && typeof obj.uid === 'string' && 'currentWeek' in obj && typeof obj.currentWeek === 'number'
}

const Toast = ({ appearance = 'error', autoDismissTimeout, children, onDismiss, ...props }: ExtendedToastProps) => {
  const isMonthlySummaryNotification = 'data' in props && isValidMonthlySummaryData(props.data)

  // Auto-dismiss for regular toasts only (MonthlySummaryToast handles its own via Snackbar)
  React.useEffect(() => {
    if (isMonthlySummaryNotification || !autoDismissTimeout || autoDismissTimeout <= 0) {
      return undefined
    }

    const timer = setTimeout(() => {
      onDismiss()
    }, autoDismissTimeout)

    return () => clearTimeout(timer)
  }, [isMonthlySummaryNotification, autoDismissTimeout, onDismiss])

  if (isMonthlySummaryNotification) {
    const monthlySummaryProps = props as ExtendedToastProps
    return (
      <div style={{ pointerEvents: 'auto' }}>
        <MonthlySummaryToast
          appearance={appearance}
          autoDismissTimeout={autoDismissTimeout}
          onDismiss={onDismiss}
          data={monthlySummaryProps.data}
          onSnooze={monthlySummaryProps.onSnooze}
          onCreateMonthlySummaryNote={monthlySummaryProps.onCreateMonthlySummaryNote}
        >
          {children}
        </MonthlySummaryToast>
      </div>
    )
  }

  // Default toast behavior for regular notifications
  return (
    <div style={{ pointerEvents: 'auto' }}>
      <Alert severity={appearance} elevation={6} onClose={() => onDismiss()} sx={{ minWidth: '300px' }}>
        {children}
      </Alert>
    </div>
  )
}

const ToastContainer = ({ children }: ToastContainerProps) => (
  <div
    style={{
      position: 'fixed',
      bottom: '16px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      alignItems: 'center',
      pointerEvents: 'none',
    }}
  >
    {children}
  </div>
)

export const ToastProvider = ({ children }: ToastProviderProps) => (
  <ToastNotificationsToastProvider autoDismissTimeout={15000} components={{ Toast, ToastContainer }}>
    {children}
  </ToastNotificationsToastProvider>
)
